import { Trans } from 'next-i18next';
import { CSSProperties } from 'react';
import { TFunction } from 'i18next';

/**
 * Type definition for supported HTML element styles
 * @typedef {Object} HtmlTransStyleOVerrides
 * @property {CSSProperties} [p] - Styles for the <p> element.
 * @property {CSSProperties} [span] - Styles for the <span> element.
 * @property {CSSProperties} [h1] - Styles for the <h1> element.
 * @property {CSSProperties} [h2] - Styles for the <h2> element.
 * @property {CSSProperties} [h3] - Styles for the <h3> element.
 * @property {CSSProperties} [h4] - Styles for the <h4> element.
 * @property {CSSProperties} [h5] - Styles for the <h5> element.
 * @property {CSSProperties} [h6] - Styles for the <h6> element.
 * @property {CSSProperties} [ul] - Styles for the <ul> element.
 * @property {CSSProperties} [ol] - Styles for the <ol> element.
 * @property {CSSProperties} [li] - Styles for the <li> element.
 * @property {CSSProperties} [em] - Styles for the <em> element.
 * @property {CSSProperties} [strong] - Styles for the <strong> element.
 * @property {CSSProperties} [a] - Styles for the <a> element.
 * @property {CSSProperties} [code] - Styles for the <code> element.
 * @property {CSSProperties} [pre] - Styles for the <pre> element.
 * @property {CSSProperties} [br] - Styles for the <br> element.
 */
export type HtmlTransStyleOVerrides = {
  p?: CSSProperties;
  b?: CSSProperties;
  span?: CSSProperties;
  i?: CSSProperties;
  h1?: CSSProperties;
  h2?: CSSProperties;
  h3?: CSSProperties;
  h4?: CSSProperties;
  h5?: CSSProperties;
  h6?: CSSProperties;
  ul?: CSSProperties;
  ol?: CSSProperties;
  li?: CSSProperties;
  em?: CSSProperties;
  strong?: CSSProperties;
  a?: CSSProperties;
  code?: CSSProperties;
  pre?: CSSProperties;
  br?: CSSProperties;
};

export type HtmlTransClassNames = {
  p?: string;
  b?: string;
  span?: string;
  i?: string;
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  h5?: string;
  h6?: string;
  ul?: string;
  ol?: string;
  li?: string;
  em?: string;
  strong?: string;
  a?: string;
  code?: string;
  pre?: string;
  br?: string;
};

/**
 * Type definition for interpolation values
 * @typedef {Object} HtmlTransValues
 * @property {string | number | boolean} [key] - Any string key with a value that can be interpolated into the translation string
 */
export type HtmlTransValues = {
  [key: string]: string | number | boolean;
};

/**
 * Props for the HtmlTrans component
 * @typedef {Object} HtmlTransProps
 * @property {TFunction} [t] - The translation function provided by `next-i18next`.
 * @property {string} [i18nKey] - The key for the translation string.
 * @property {supportedStyles} [supportedStyles={}] - An object containing styles for supported HTML elements.
 * @property {HtmlTransValues} [values] - An object containing values to interpolate into the translation string.
 * @property {string} [defaultValue] - The default value for the translation string.
 */
type HtmlTransProps = {
  t?: TFunction;
  i18nKey?: string;
  classNames?: {
    [key: string]: string;
  };
  styleOverrides?: HtmlTransStyleOVerrides;
  values?: HtmlTransValues;
  defaultValue?: string;
};

/**
 * A utility component that parses basic html content from the translation files with optional
 * styling for various HTML elements.
 *
 * @example
 * ```tsx
 * <HtmlTrans
 *   i18nKey="welcome.message"
 *   values={{ name: "John" }}
 *   styleOverrides={{
 *     p: { color: 'blue' },
 *     h1: { fontWeight: 'bold' }
 *   }}
 * />
 * ```
 *
 * @param {TFunction} [t] - The translation function provided by `next-i18next`. If not provided, the component will use the default translation function.
 * @param {string} [i18nKey] - The key for the translation string. This key should correspond to a translation in your i18n configuration.
 * @param {classNames} [classNames={}] - An object containing class names for supported HTML elements. Each key corresponds to an HTML element that can be styled.
 * @param {styleOverrides} [styleOverrides={}] - An object containing styles for supported HTML elements. Each key corresponds to an HTML element that can be styled.
 * @param {HtmlTransValues} [values] - An object containing values to interpolate into the translation string.
 *
 * @returns {JSX.Element} The rendered `Trans` component with applied styles.
 */
export default function HtmlTrans({ t, i18nKey /*= 'key not found'*/, classNames = {}, styleOverrides = {}, values = {}, defaultValue = '' }: HtmlTransProps): JSX.Element {
  return <Trans t={t} i18nKey={i18nKey} values={values} defaultValue={defaultValue}
    components={{
      p: <p className={classNames.p} style={styleOverrides.p} />,
      b: <b className={classNames.b} style={styleOverrides.b} />,
      i: <i className={classNames.i} style={styleOverrides.i} />,
      span: <span className={classNames.span} style={styleOverrides.span} />,
      h1: <h1 className={classNames.h1} style={styleOverrides.h1} />,
      h2: <h2 className={classNames.h2} style={styleOverrides.h2} />,
      h3: <h3 className={classNames.h3} style={styleOverrides.h3} />,
      h4: <h4 className={classNames.h4} style={styleOverrides.h4} />,
      h5: <h5 className={classNames.h5} style={styleOverrides.h5} />,
      h6: <h6 className={classNames.h6} style={styleOverrides.h6} />,
      ul: <ul className={classNames.ul} style={styleOverrides.ul} />,
      ol: <ol className={classNames.ol} style={styleOverrides.ol} />,
      li: <li className={classNames.li} style={styleOverrides.li} />,
      em: <em className={classNames.em} style={styleOverrides.em} />,
      strong: <strong className={classNames.strong} style={styleOverrides.strong} />,
      a: <a className={classNames.a} style={styleOverrides.a} />,
      code: <code className={classNames.code} style={styleOverrides.code} />,
      pre: <pre className={classNames.pre} style={styleOverrides.pre} />,
      br: <br className={classNames.br} style={styleOverrides.br} />,
    }}/>;
}